import { Injectable } from '@angular/core';
import { BrowserStorageService } from '../../services/browser-storage.service';
import { EncryptionService } from '../../services/encryption.service';
import { environment } from 'src/environments/environment';
import { BusinessLine, businessLinesFromGestores } from '@app/domain/models/dashboard-lines.model';
import { tieneAutoridadPayments } from '@app/domain/models/payments/payments-authority';
import { SERVICIOS_SPEI_ESTRICTO, servicioDeGestor } from '@app/domain/models/gestor-servicio';
import { TasaBulkLevel } from '@app/domain/models/tpv/tasas-bulk/tasas-bulk.model';
import { allowedTasaBulkLevels } from '@app/domain/models/tpv/tasas-bulk/tasas-bulk-access';

export type TpvRoleName = 'SUPER_ADMIN' | 'ADMINISTRADOR' | 'OPERADOR';

/**
 * Lo que los predicados de acceso leen de un gestor de la sesión (`user.gestores[]`).
 * Tipado mínimo y estructural: el objeto viene del verify y puede traer más campos.
 */
interface GestorDeSesion {
  readonly service?: unknown;
  readonly provider?: unknown;
}

/**
 * Línea de negocio derivada de los gestores del usuario (Dashboard Global). Vive en
 * el dominio (feature 108); se re-exporta para no romper los imports existentes.
 */
export type { BusinessLine };

/** Nivel jerárquico del gestor TPV (lo emite backend en `gestores[].scope_level`). */
export type TpvScopeLevel = 'FATHER' | 'EMPRESA' | 'UNIDAD_NEGOCIO' | 'COMERCIO';

/** "Transacciones en vivo" de E-commerce: oculta en el front (humano, 2026-10-05). */
const PAYMENTS_LIVE_VISIBLE: boolean = false;

@Injectable({ providedIn: 'root' })
export class UserRoleService {
  private readonly BALANCE_OBJECT_KEY = 'balance_object';
  private readonly ACCESS_TOKEN_KEY = 'access';

  /** Jerarquía de scope_level: FATHER es el nivel más alto, COMERCIO el más bajo. */
  private readonly TPV_SCOPE_RANK: Record<TpvScopeLevel, number> = {
    COMERCIO: 1,
    UNIDAD_NEGOCIO: 2,
    EMPRESA: 3,
    FATHER: 4,
  };

  constructor(
    private browserStorage: BrowserStorageService,
    private encryption: EncryptionService
  ) {}

  /**
   * Rol TPV del usuario. Prioriza el gestor con `service === 'TPV'` del objeto
   * user (fuente real: el verify ahora lo entrega) mapeando su `role.id`; si no
   * hay gestor TPV, cae al claim `tpv_role_name` del JWT (compatibilidad).
   */
  getTpvRoleName(): TpvRoleName | null {
    const fromGestor = this.mapTpvRoleId(this.getTpvGestor()?.role?.id);
    if (fromGestor) return fromGestor;

    const encryptedToken = this.browserStorage.getSessionStorageItem(this.ACCESS_TOKEN_KEY);
    if (!encryptedToken) return null;
    try {
      const token = this.encryption.decrypt(encryptedToken);
      if (!token) return null;
      const parts = token.split('.');
      if (parts.length !== 3) return null;

      // base64url -> base64 + padding
      const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
      const padded = base64.padEnd(base64.length + (4 - (base64.length % 4)) % 4, '=');
      const bytes = Uint8Array.from(atob(padded), c => c.charCodeAt(0));
      const json = JSON.parse(new TextDecoder().decode(bytes));
      const role = (json?.tpv_role_name ?? json?.user?.tpv_role_name) as string | undefined;
      if (role === 'SUPER_ADMIN' || role === 'ADMINISTRADOR' || role === 'OPERADOR') {
        return role;
      }
      return null;
    } catch {
      return null;
    }
  }

  isTpvSuperAdmin(): boolean {
    return this.getTpvRoleName() === 'SUPER_ADMIN';
  }

  isTpvAdministrador(): boolean {
    return this.getTpvRoleName() === 'ADMINISTRADOR';
  }

  isTpvOperador(): boolean {
    return this.getTpvRoleName() === 'OPERADOR';
  }

  /**
   * Indica si el usuario tiene acceso al módulo TPV: existe un gestor de servicio TPV
   * (normalizado: `' tpv '` cuenta) en el objeto user. El sidebar lo usa para
   * mostrar/ocultar el módulo TPV (no basta el feature flag del tenant).
   */
  hasTpvAccess(): boolean {
    return this.getTpvGestor() !== null;
  }

  /**
   * Indica si el usuario tiene un gestor de servicio PAYMENTS (E-commerce) en el
   * objeto user. Se normaliza igual que los demás predicados (`trim().toUpperCase()`):
   * sin eso un `' payments '` contaba como línea en `getBusinessLines()` pero no daba
   * acceso al módulo.
   */
  hasPaymentsAccess(): boolean {
    return this.getGestores().some((g: GestorDeSesion) => servicioDeGestor(g?.service) === 'PAYMENTS');
  }

  /**
   * E-commerce USABLE: gestor PAYMENTS y el módulo encendido en el AMBIENTE
   * (`environment.featureFlags.ecommerce`, el kill-switch del deploy; ausente =
   * encendido). NO depende del rol SPEI de la entidad activa ni de las `features` del
   * tenant (el tenant es solo rebranding — decisión del humano 2026-10-07). Es la regla
   * única del módulo: la ruta `/e-commerce`, su dashboard y la línea "Pasarela de Pagos".
   */
  canUseEcommerce(): boolean {
    return (environment.featureFlags?.ecommerce ?? true) && this.hasPaymentsAccess();
  }

  /** Alta de comercio en E-commerce: gestor PAYMENTS con rol 1 o 7 (C11). Ver el predicado. */
  canCreatePaymentsCompany(): boolean {
    return tieneAutoridadPayments(this.getGestores());
  }

  /**
   * Transacciones LIVE de la pasarela (todos los comercios). OCULTA por decisión del humano
   * (2026-10-05): solo en el front, mientras el back ajusta su permiso. Al habilitarla
   * (`PAYMENTS_LIVE_VISIBLE = true`) la regla es tener asignado el gestor PAYMENTS, sin
   * importar su rol. El submenú y la guarda de la ruta leen este método.
   */
  canReadPaymentsLive(): boolean {
    return PAYMENTS_LIVE_VISIBLE && this.hasPaymentsAccess();
  }

  /**
   * Indica si el usuario tiene un gestor de SPEI: una cuenta transaccional
   * (provider STP / Transfer / Arcus), es decir, cualquier gestor cuyo
   * `service` NO sea 'TPV'. Solo estas cuentas tienen saldo (rieles SPEI);
   * las de TPV no, por eso sin gestor SPEI no hay datos de saldo que mostrar.
   *
   * ⚠️ Se deja SIN normalizar y sin la lista de servicios conocidos a propósito: es la
   * semántica "tiene saldo" del selector de entidad (lo usa E-commerce) y cambiarla está
   * fuera de esta feature. Un servicio desconocido —o un `' tpv '` con espacios— cuenta
   * aquí como "tiene saldo" aunque no dé ninguna línea.
   */
  hasSpeiGestor(): boolean {
    return this.getGestores().some((g: any) => (g?.service ?? '') !== 'TPV');
  }

  /**
   * El saldo de cuenta solo tiene sentido en el dashboard de TPV cuando el
   * usuario gestiona TPV y además una cuenta SPEI (con saldo). Si no hay gestor
   * SPEI, no hay datos de saldo y la barra debe ocultarse.
   */
  canShowTpvAccountBalance(): boolean {
    return this.hasTpvAccess() && this.hasSpeiGestor();
  }

  /**
   * Gestor de SPEI en sentido ESTRICTO: `service === 'SPEI'` o gestor legacy
   * sin `service`. Distinto de `hasSpeiGestor()`, que agrupa CARDS y PAYMENTS
   * a proposito (semantica de "tiene saldo") y por eso NO sirve para decidir si
   * ofrecer el producto SPEI: con esa regla un usuario solo-Cards lo veria.
   */
  hasSpeiService(): boolean {
    return this.getGestores().some((g: GestorDeSesion) => {
      const service = servicioDeGestor(g?.service);
      return service !== null && SERVICIOS_SPEI_ESTRICTO.has(service);
    });
  }

  /**
   * Indica si el usuario tiene un gestor de CARDS (`service === 'CARDS'`).
   * El sidebar lo usa para mostrar/ocultar el módulo Tarjetas en Envíos
   * (no basta el feature flag del tenant).
   */
  hasCardsGestor(): boolean {
    return this.getGestores().some((g: GestorDeSesion) => servicioDeGestor(g?.service) === 'CARDS');
  }

  /**
   * Indica si el usuario tiene la línea SPEI ("Envíos › SPEI": su dashboard,
   * Unidades y cuentas, Transacciones, Fondeo). Es la línea `spei` de la regla ÚNICA
   * del dominio (feature 108): SPEI o gestor legado sin `service`, en sentido
   * ESTRICTO — un usuario solo de Cards NO la tiene (banca le responde 403 a Envíos
   * desde la feature 98) y PAYMENTS tampoco.
   *
   * Merge de dev (2026-10-08): dev la definió agrupando CARDS con SPEI; en esta rama
   * prevalece la regla de la feature 108 (Envíos solo con SPEI estricto).
   *
   * No es `hasSpeiGestor()`: esa cuenta PAYMENTS como SPEI por la semántica de
   * "tiene saldo" (el selector de entidad que usa E-commerce depende de ella).
   */
  hasSpeiLine(): boolean {
    return this.getBusinessLines().includes('spei');
  }

  /**
   * Indica si el usuario tiene al menos una línea de negocio CON dashboard (SPEI,
   * Tarjetas, TPV o la Pasarela de Pagos): la misma regla que `dashboardEntryRoute`.
   */
  hasDashboard(): boolean {
    return this.getBusinessLines().length > 0;
  }

  /**
   * `role.id` de cada gestor del usuario con `service === 'CARDS'`. Espejo de
   * `getTpvAuthorities()`: el rol de un servicio lo da SU gestor, nunca la
   * entidad activa de SPEI (`balance_object`), que es de otro contexto —
   * `getRoleId()` resuelve contra esa clave y por eso no sirve para Cards.
   * Se descartan los `role.id` no numéricos (payload viejo o incompleto).
   *
   * Solo gestores ACTIVOS: banca ya manda en la sesión solo `gestor.is_active AND
   * cp.is_active`, pero un gestor que llegue con `is_active: false` explícito NO
   * concede (mismo criterio que `payments-authority`; banca 59274c8 responde 403
   * `cards_role_required` sin un gestor de Cards activo). Ausente = activo (payload
   * de siempre).
   */
  private getCardsRoleIds(): number[] {
    return this.getGestores()
      .filter((g: any) => String(g?.service ?? '').trim().toUpperCase() === 'CARDS' && g?.is_active !== false)
      .map((g: any) => Number(g?.role?.id))
      .filter((id: number) => Number.isFinite(id));
  }

  /**
   * Indica si el usuario tiene alguno de los roles dados EN CARDS. El sidebar
   * lo usa para los submódulos completos de Tarjetas (rol 1 Super Admin o 7
   * Usuario de Aprobación). Sin gestor de Cards, sin `role.id` legible o con la
   * sesión ilegible devuelve false: el default seguro de un gate financiero es
   * NO conceder.
   */
  hasCardsRole(...roleIds: number[]): boolean {
    const wanted = new Set<number>(roleIds);
    return this.getCardsRoleIds().some((id: number) => wanted.has(id));
  }

  /**
   * ¿Tiene ALGÚN rol de Cards (1–7) en un gestor activo? Banca (59274c8) exige un gestor
   * de Cards activo para administrar gestores de Cards (directorio, alta, cambios,
   * historial y actividad con servicio CARDS) y para leer la numeración de empresas:
   * sin él responde 403 `cards_role_required`. Las pantallas no ofrecen lo que se negaría.
   */
  hasAnyCardsRole(): boolean {
    return this.hasCardsRole(1, 2, 3, 4, 5, 6, 7);
  }

  /**
   * Proveedores SPEI ASIGNADOS al usuario (el `provider` de sus gestores con
   * `service === 'SPEI'`; los gestores legacy sin `service` cuentan como SPEI),
   * en orden de aparición y sin duplicados. El dashboard y el sidebar los usan
   * para NO ofrecer proveedores que el usuario no tiene asignados (p. ej. el
   * provider POMELO de un gestor CARDS no es un proveedor SPEI).
   */
  getSpeiProviders(): string[] {
    const seen = new Set<string>();
    const result: string[] = [];
    for (const g of this.getGestores() as GestorDeSesion[]) {
      const service = servicioDeGestor(g?.service);
      if (service === null || !SERVICIOS_SPEI_ESTRICTO.has(service)) continue;
      const provider = String(g?.provider ?? '').trim();
      if (!provider || seen.has(provider)) continue;
      seen.add(provider);
      result.push(provider);
    }
    return result;
  }

  /**
   * Líneas de negocio del usuario, derivadas del `service` de sus gestores con la
   * regla ÚNICA del dominio (`businessLinesFromGestores`, feature 108): 'TPV' →
   * tpv, 'CARDS' → cards, 'SPEI' o vacío → spei, 'PAYMENTS' → payments (la Pasarela
   * de Pagos, solo con E-commerce encendido en el ambiente; merge de dev) y NUNCA
   * spei (antes contaba como SPEI y llevaba a un Envíos con 403); cualquier otro →
   * ninguna.
   *
   * Nota: distinto de `hasSpeiGestor()`, que agrupa CARDS y PAYMENTS con SPEI a
   * propósito (semántica de "tiene saldo") y NO cambia.
   */
  getBusinessLines(): BusinessLine[] {
    return businessLinesFromGestores(this.getGestores(), {
      paymentsEnabled: environment.featureFlags?.ecommerce ?? true,
    });
  }

  /** Lee el gestor TPV (servicio normalizado) del objeto user en sessionStorage. */
  private getTpvGestor(): { role?: { id?: number } } | null {
    return this.getGestores().find((g: GestorDeSesion) => this.esGestorTpv(g)) ?? null;
  }

  /** Gestor de servicio TPV, normalizado igual en TODOS los predicados TPV. */
  private esGestorTpv(gestor: GestorDeSesion | null | undefined): boolean {
    return servicioDeGestor(gestor?.service) === 'TPV';
  }

  /**
   * Nivel jerárquico más alto entre los gestores TPV del usuario. El sidebar lo
   * usa para ocultar Empresas/Unidades (matriz "nivel asignado y hacia abajo"):
   * COMERCIO solo ve Comercios; UNIDAD_NEGOCIO ve Unidades+Comercios; EMPRESA ve
   * todo. Con varios gestores TPV se toma el nivel más alto. Si ninguno trae
   * `scope_level` (payload viejo), devuelve null → no se restringe (compatibilidad).
   */
  getTpvScopeLevel(): TpvScopeLevel | null {
    const levels = this.getGestores()
      .filter((g: GestorDeSesion) => this.esGestorTpv(g))
      .map((g: any) => this.normalizeScopeLevel(g?.scope_level))
      .filter((l: TpvScopeLevel | null): l is TpvScopeLevel => l !== null);

    if (!levels.length) return null;
    return levels.reduce((best, level) =>
      this.TPV_SCOPE_RANK[level] > this.TPV_SCOPE_RANK[best] ? level : best,
    );
  }

  /** Normaliza el `scope_level` del backend al enum cerrado (o null si no es válido). */
  private normalizeScopeLevel(value: unknown): TpvScopeLevel | null {
    const v = String(value ?? '').trim().toUpperCase();
    if (v === 'FATHER' || v === 'CTA_PADRE') return 'FATHER';
    return v === 'EMPRESA' || v === 'UNIDAD_NEGOCIO' || v === 'COMERCIO' ? v : null;
  }

  /** Lista de gestores del objeto user en sessionStorage (o vacío si no hay). */
  private getGestores(): any[] {
    const encrypted = this.browserStorage.getSessionStorageItem('user');
    if (!encrypted) return [];
    try {
      const decrypted = this.encryption.decrypt(encrypted);
      const user = JSON.parse(decrypted);
      const first = Array.isArray(user) ? user[0] : user;
      return Array.isArray(first?.gestores) ? first.gestores : [];
    } catch {
      return [];
    }
  }

  // ── Autoridad TPV (matriz rol × nivel, julio 2026) ────────────────────────
  // La autoridad la da el GESTOR (por servicio) y el rol 1-7 dice cuánta; el
  // catálogo de roles es único en toda la banca. El candado rol↔nivel lo valida
  // el back en el alta: father {1,6,7} · empresa/unidad {2,3} · comercio {4,5}.
  // Escritura: 1/7 (father, todo el subtree), 2 (su empresa o su unidad),
  // 4 (su comercio). 6/3/5 son solo-lectura de su nivel.

  /** Rol y nivel de cada gestor TPV del usuario. */
  private getTpvAuthorities(): { roleId: number; level: TpvScopeLevel | null }[] {
    return this.getGestores()
      .filter((g: GestorDeSesion) => this.esGestorTpv(g))
      .map((g: any) => {
        const roleId = Number(g?.role?.id);
        return {
          roleId,
          level: this.normalizeScopeLevel(g?.scope_level) ?? this.inferLevelFromRole(roleId),
        };
      })
      .filter((a: { roleId: number }) => Number.isFinite(a.roleId));
  }

  /**
   * Con el candado rol↔nivel, el rol permite inferir el nivel cuando el payload
   * no trae `scope_level`: 1/6/7 solo existen a nivel father y 4/5 a nivel
   * comercio. 2/3 son ambiguos (empresa o unidad) → null.
   */
  private inferLevelFromRole(roleId: number): TpvScopeLevel | null {
    if (roleId === 1 || roleId === 6 || roleId === 7) return 'FATHER';
    if (roleId === 4 || roleId === 5) return 'COMERCIO';
    return null;
  }

  /** Escritura father (roles 1/7): todo el subtree, incluida la creación de empresas. */
  private isTpvFatherWriter(): boolean {
    return this.getTpvAuthorities().some(a => a.roleId === 1 || a.roleId === 7);
  }

  /**
   * Escritura a nivel empresa (rol 2 con scope EMPRESA): provisiona comercios y
   * crea/edita unidades de su empresa. El rol 2 a nivel UNIDAD_NEGOCIO NO crea
   * (autoridad fina: no puede expandir su propio alcance). Rol 2 sin scope_level
   * (payload viejo) se trata como empresa por compatibilidad.
   */
  private isTpvCompanyWriter(): boolean {
    return this.getTpvAuthorities().some(
      a => a.roleId === 2 && (a.level === 'EMPRESA' || a.level === null)
    );
  }

  /**
   * Rol global de banca 1/2 entre todos los gestores activos (equivale al
   * IsAdmin del back): habilita lo exclusivo del admin global — alta masiva de
   * comercios, usuarios globales y alta de liquidaciones.
   */
  isGlobalBankingAdmin(): boolean {
    return this.getGestores().some((g: any) => {
      const id = Number(g?.role?.id);
      return id === 1 || id === 2;
    });
  }

  /**
   * Brazo aditivo mientras el back tiene TPV_ENFORCE_COMPANY_SCOPE apagado:
   * rol global de banca 1/2 = escritura total en TPV. Al prender
   * `featureFlags.tpvEnforceCompanyScope` (en par con el back) este brazo
   * desaparece y queda solo la matriz rol × nivel del gestor TPV.
   */
  private hasTpvGlobalWriteArm(): boolean {
    if (environment.featureFlags?.tpvEnforceCompanyScope) return false;
    return this.isGlobalBankingAdmin();
  }

  /**
   * Compatibilidad con sesiones viejas sin `gestores` en el objeto user: usa el
   * claim `tpv_role_name` del JWT (SUPER_ADMIN/ADMINISTRADOR escriben). Solo
   * aplica cuando no hay ningún gestor TPV del cual derivar autoridad.
   */
  private hasLegacyTpvWriteClaim(): boolean {
    if (this.getTpvAuthorities().length) return false;
    const legacy = this.getTpvRoleName();
    return legacy === 'SUPER_ADMIN' || legacy === 'ADMINISTRADOR';
  }

  /** Crear empresas TPV: solo escritura father (1/7), más el brazo global. */
  canCreateTpvCompany(): boolean {
    return this.isTpvFatherWriter() || this.hasTpvGlobalWriteArm() || this.hasLegacyTpvWriteClaim();
  }

  /** Crear unidades de negocio: father (1/7) o rol 2 a nivel empresa, más el brazo global. */
  canCreateTpvBusinessUnit(): boolean {
    return this.isTpvFatherWriter() || this.isTpvCompanyWriter()
      || this.hasTpvGlobalWriteArm() || this.hasLegacyTpvWriteClaim();
  }

  /** Provisionar comercios (alta individual): father (1/7) o rol 2 a nivel empresa, más el brazo global. */
  canCreateTpvMerchant(): boolean {
    return this.isTpvFatherWriter() || this.isTpvCompanyWriter()
      || this.hasTpvGlobalWriteArm() || this.hasLegacyTpvWriteClaim();
  }

  /**
   * Capturar la Tasa Comercio (feature 156, R51): «escritura sobre la empresa»
   * —father 1/7 o rol 2 de nivel empresa—, más el brazo global y la
   * compatibilidad legada. El back responde 403 «Sin autoridad para capturar la
   * tasa» a un acceso de comercio o de unidad aunque también lea la empresa.
   *
   * Hoy es el mismo predicado que `canCreateTpvMerchant()`, pero responde OTRA
   * pregunta (la regla de §Captura individual del contrato) y puede divergir: por
   * eso tiene nombre propio. La carga masiva de Comercios usa este mismo.
   */
  canCaptureTpvMerchantTasa(): boolean {
    return this.isTpvFatherWriter() || this.isTpvCompanyWriter()
      || this.hasTpvGlobalWriteArm() || this.hasLegacyTpvWriteClaim();
  }

  /**
   * Niveles de la carga masiva de comisiones TPV que el usuario puede usar
   * (feature 156, R6), en el orden de las pestañas: Empresas = crear empresas,
   * Unidades = crear unidades, Comercios = capturar la Tasa Comercio; recortado
   * por el alcance del gestor. El back sigue siendo la autoridad de los permisos TPV.
   *
   * Solo para `is_staff`, ADEMÁS de los permisos TPV (decisión del humano 2026-10-21): sin
   * staff no hay niveles, así que la guarda, el submenú «Comisiones» y las pestañas lo heredan
   * de este único sitio. Es un gate de PRESENTACIÓN: banca NO restringe por `is_staff` (riesgo
   * aceptado por el humano: un rol TPV podría llamar los endpoints directamente). La captura
   * individual de la Tasa Comercio NO pasa por aquí: sigue con `canCaptureTpvMerchantTasa()`.
   */
  tpvBulkCommissionLevels(): TasaBulkLevel[] {
    if (!this.isPlatformStaff()) return [];
    return allowedTasaBulkLevels(
      {
        company: this.canCreateTpvCompany(),
        unit: this.canCreateTpvBusinessUnit(),
        merchant: this.canCaptureTpvMerchantTasa(),
      },
      this.getTpvScopeLevel(),
    );
  }

  /** Alta masiva de comercios: exclusiva del admin global (sin brazo scoped), aun con el scope prendido. */
  canCreateTpvMerchantsBulk(): boolean {
    return this.isGlobalBankingAdmin();
  }

  /**
   * Mapea el `role.id` del gestor TPV al nombre de rol usado en el front.
   * El catálogo real es 1-7 (ver matriz rol × nivel): 7 es escritura father
   * equivalente al 1, y 5/6 son solo-lectura, por lo que caen en OPERADOR para
   * los gates existentes (edición/sidebar). El 4 (escritura de su comercio) no
   * tiene equivalente en la tríada vieja → null.
   */
  private mapTpvRoleId(roleId: number | null | undefined): TpvRoleName | null {
    switch (Number(roleId)) {
      case 1: return 'SUPER_ADMIN';
      case 7: return 'SUPER_ADMIN';
      case 2: return 'ADMINISTRADOR';
      case 3: return 'OPERADOR';
      case 5: return 'OPERADOR';
      case 6: return 'OPERADOR';
      default: return null;
    }
  }

  /**
   * Obtiene la entidad actualmente seleccionada desde balance_object.
   */
  private getActiveEntity(): any | null {
    const encrypted = this.browserStorage.getSessionStorageItem(this.BALANCE_OBJECT_KEY);
    if (!encrypted) return null;
    try {
      const decrypted = this.encryption.decrypt(encrypted);
      const parsed = JSON.parse(decrypted);
      return Array.isArray(parsed) ? parsed[0] : parsed;
    } catch {
      return null;
    }
  }

  /**
   * Obtiene el rol del usuario para la entidad actualmente seleccionada.
   * Prioridad:
   * 1. gestor_role_id en la entidad activa (etiquetado al resolver gestores)
   * 2. Mapa gestor_roles con provider::entity_type
   * 3. Match por entity_type en user.gestores
   * 4. user.role.id (estructura antigua)
   * 5. Primer gestor como fallback
   */
  getRoleId(): number | null {
    const activeEntity = this.getActiveEntity();

    // 1. Rol etiquetado directamente en la entidad
    if (activeEntity?.gestor_role_id != null) {
      return Number(activeEntity.gestor_role_id);
    }

    // 2. Buscar en el mapa gestor_roles (provider::entity_type → role_id)
    const entityType = (activeEntity?.entity_type ?? '').toString().trim();
    const provider = (activeEntity?.provider ?? '').toString().trim();
    if (entityType && provider) {
      const roleFromMap = this.getRoleFromMap(provider, entityType);
      if (roleFromMap !== null) return roleFromMap;
    }

    // 3-5. Fallback: buscar en user.gestores o user.role
    return this.getRoleFromUserObject(entityType, provider);
  }

  /**
   * Busca el rol en el mapa gestor_roles almacenado en sessionStorage.
   * Las entidades hijas (Empresa, Unidad de Negocio, Cuenta) heredan
   * el rol de su Cta_Padre del mismo provider si no tienen match directo.
   */
  private getRoleFromMap(provider: string, entityType: string): number | null {
    const encrypted = this.browserStorage.getSessionStorageItem('gestor_roles');
    if (!encrypted) return null;
    try {
      const decrypted = this.encryption.decrypt(encrypted);
      const map: Record<string, number> = JSON.parse(decrypted);

      // 1. Match directo: provider::entity_type
      const directKey = `${provider}::${entityType}`;
      const directRole = map[directKey];
      if (directRole != null) return Number(directRole);

      // 2. Herencia: entidades hijas heredan el rol de su Cta_Padre del mismo provider
      const parentKey = `${provider}::Cta_Padre`;
      const parentRole = map[parentKey];
      if (parentRole != null) return Number(parentRole);

      return null;
    } catch {
      return null;
    }
  }

  /**
   * Busca el rol en el objeto user (gestores o role directo).
   */
  private getRoleFromUserObject(entityType: string, provider: string): number | null {
    const encrypted = this.browserStorage.getSessionStorageItem('user');
    if (!encrypted) return null;
    try {
      const decrypted = this.encryption.decrypt(encrypted);
      const user = JSON.parse(decrypted);
      const first = Array.isArray(user) ? user[0] : user;

      // Estructura antigua: user.role.id
      const directRoleId = first?.role?.id;
      if (directRoleId != null) return Number(directRoleId);

      // Estructura con gestores
      const gestores = first?.gestores ?? [];
      if (!Array.isArray(gestores) || gestores.length === 0) return null;

      // Match por provider + entity_type (más preciso)
      if (entityType && provider) {
        const exactMatch = gestores.find((g: any) =>
          this.normalize(g?.entity_type ?? '') === this.normalize(entityType) &&
          (g?.provider ?? '') === provider
        );
        if (exactMatch?.role?.id != null) return Number(exactMatch.role.id);
      }

      // Herencia: entidades hijas heredan el rol de su Cta_Padre del mismo provider
      if (provider) {
        const parentMatch = gestores.find((g: any) =>
          this.normalize(g?.entity_type ?? '') === 'cta_padre' &&
          (g?.provider ?? '') === provider
        );
        if (parentMatch?.role?.id != null) return Number(parentMatch.role.id);
      }

      // Match solo por entity_type
      if (entityType) {
        const typeMatch = gestores.find((g: any) =>
          this.normalize(g?.entity_type ?? '') === this.normalize(entityType)
        );
        if (typeMatch?.role?.id != null) return Number(typeMatch.role.id);
      }

      // Fallback: primer gestor
      const fallback = gestores[0]?.role?.id;
      return fallback != null ? Number(fallback) : null;
    } catch {
      return null;
    }
  }

  private normalize(value: string): string {
    return value.replace(/\s+/g, ' ').trim().toLowerCase();
  }

  hasRole(...roleIds: number[]): boolean {
    const current = this.getRoleId();
    return current !== null && roleIds.includes(current);
  }

  isApprover(): boolean {
    return this.hasRole(7);
  }

  /**
   * Puerta de las pantallas de Cards para LIBERAR órdenes SPEI salidas de la
   * gemela de un nodo de Cards ("Solicitudes por aprobar", su conteo y los
   * enlaces a Pendientes). Espejo de `clabes_liberables_por` de banca tras
   * "Cards y SPEI SEPARADOS" (2026-10-12): libera el rol 7 de SPEI (global) O
   * el rol 7 de Cards sobre la cuenta padre que contiene el nodo.
   *
   * `isApprover()` solo mira la entidad SPEI activa (`balance_object`), así que
   * un rol 7 de Cards con otro rol en SPEI se quedaba sin la puerta aunque
   * banca sí le listara sus órdenes. No sustituye a banca: ella acota por
   * alcance y decide `puede_liberar` por fila; esto solo enseña la puerta.
   * `hasCardsRole(7)` es GLOBAL (no está atado a la cuenta padre del nodo
   * activo): fuera de su alcance la puerta se ve pero el conteo da 0, y es
   * banca quien decide qué se libera.
   * No usar en SPEI/Suite (allí sigue `isApprover()`).
   */
  canReleaseCardsSpeiOrders(): boolean {
    return this.hasCardsRole(7) || this.isApprover();
  }

  /**
   * Correo del usuario autenticado (del objeto user en sessionStorage). Se usa
   * en el control dual de transferencias para identificar al capturador: si el
   * `user_email` de la transacción coincide con este correo, el usuario actual
   * fue quien la capturó y no puede liberarla/rechazarla (RN01).
   */
  getCurrentUserEmail(): string | null {
    const encrypted = this.browserStorage.getSessionStorageItem('user');
    if (!encrypted) return null;
    try {
      const decrypted = this.encryption.decrypt(encrypted);
      const user = JSON.parse(decrypted);
      const first = Array.isArray(user) ? user[0] : user;
      const email = first?.email ?? first?.correo ?? first?.username ?? null;
      return email ? String(email).trim().toLowerCase() : null;
    } catch {
      return null;
    }
  }

  /**
   * Control dual (RN01): el backend marca al autorizador como EXENTO
   * (`autorizador_exento: true` en TransaccionesPendientesAprobacion). Cuando es true NO
   * se requiere el double-check y el usuario puede liberar/rechazar transacciones que él
   * mismo capturó. Default false = comportamiento actual (prohíbe la auto-liberación).
   * Lo setea la pantalla que consulta pendientes; se guarda en memoria (se re-consulta
   * en cada carga), y el default seguro es NO exento si aún no se conoce.
   */
  private autorizadorExento = false;

  setAutorizadorExento(exento: boolean): void {
    this.autorizadorExento = exento === true;
  }

  isAutorizadorExento(): boolean {
    return this.autorizadorExento;
  }

  /**
   * Indica si el usuario puede ver el rastro de captura/liberación de una
   * transferencia (quién capturó y quién liberó). Solo visible para rol 1 y 7;
   * para el resto la información va censurada.
   */
  canViewTransferAudit(): boolean {
    return this.hasRole(1, 7);
  }

  isOperator(): boolean {
    return this.hasRole(3) || this.hasRole(5);
  }

  /**
   * `is_staff` del objeto user (lo entrega el verify/login). Gateaba los botones de
   * creación del módulo TPV (los `@if (isStaff)` de las pantallas TPV).
   *
   * INTERIM: la validación por `is_staff` está DESACTIVADA. Por ahora la creación en TPV
   * está SIEMPRE activa y `is_staff` NO impone ninguna restricción; todo lo que dependía
   * de este flag deja de restringir. Solo afecta a TPV (es el único módulo que lo usa).
   * Para reactivar el control dual por staff, restaurar la lectura de `user.is_staff`
   * desde sessionStorage (código anterior en el historial).
   */
  isStaff(): boolean {
    return true;
  }

  /**
   * `can_manage_gestores` del objeto user en sessionStorage (lo entrega el
   * verify/login). Es la ÚNICA señal que gatea el módulo Gestores
   * (Administración): el back ya combina `is_staff` + lista blanca de correos
   * (GESTOR_ALLOWED_EMAILS) y los endpoints /api/gestor/ responden 403 si el
   * usuario no cumple ambas. El front no debe mirar `is_staff` ni el correo
   * para esta decisión.
   */
  canManageGestores(): boolean {
    return this.userFlag('can_manage_gestores');
  }

  /**
   * `is_staff` REAL del objeto user (lo entrega el verify/login). Gatea la
   * administración de marcas (white-label): `/api/v1/tenants/` exige IsAdminUser,
   * así que sin esto la pantalla solo mostraría 403.
   *
   * Ojo: NO es `isStaff()`, que está desactivado a propósito (devuelve true) para
   * no restringir la creación en TPV. Este método sí lee la bandera.
   */
  isPlatformStaff(): boolean {
    return this.userFlag('is_staff');
  }

  /**
   * `can_manage_commissions` del objeto user. Gatea el módulo de Comisiones:
   * el back exige `is_staff` **Y** el correo en `COMMISSIONS_ALLOWED_EMAILS`
   * (lista PROPIA — definir tarifas es facultad de finanzas, no de quien
   * administra accesos), y esta bandera ya combina ambas condiciones.
   */
  canManageCommissions(): boolean {
    return this.userFlag('can_manage_commissions');
  }

  /**
   * `can_manage_tpv_gestores` del objeto user. Gemelo del anterior pero para la
   * superficie de accesos TPV, que tiene su PROPIA lista blanca en el back
   * (GESTOR_TPV_ALLOWED_EMAILS, independiente de la de SPEI). Sin esto el front
   * mostraría la pestaña/ruta TPV a quien el back va a rechazar con 403.
   *
   * Ojo: NO se deriva de `canManageGestores()` — administrar accesos SPEI no
   * concede TPV, y al revés tampoco.
   */
  canManageTpvGestores(): boolean {
    return this.userFlag('can_manage_tpv_gestores');
  }

  /**
   * @deprecated Feature 99 (Q5): administran gestores de Cards las MISMAS personas
   * que SPEI, con UN solo permiso (`can_manage_gestores`). El login sigue mandando
   * `can_manage_cards_gestores` como alias en transición, pero el front ya NO lo
   * lee: un alias ausente o falso no puede dejar en solo lectura a quien sí
   * administra gestores. Usa `canManageGestores()`; este método se borra cuando
   * banca retire el alias.
   */
  canManageCardsGestores(): boolean {
    return this.canManageGestores();
  }

  /**
   * Cuenta Polipay de la sesión (`polipay_account` del objeto user que guarda el
   * verify/login). Solo una cadena de dígitos cuenta como dato; cualquier otra
   * cosa (ausente, vacía, no numérica, sesión ilegible) ⇒ `null`: no se afirma
   * "es tu cuenta" sin dato. No se registra en ningún log.
   */
  getCurrentPolipayAccount(): string | null {
    const value = this.readUserField('polipay_account');
    if (typeof value !== 'string') return null;
    const trimmed = value.trim();
    return /^\d+$/.test(trimmed) ? trimmed : null;
  }

  /** Lee una bandera booleana del objeto user cifrado en sessionStorage. */
  private userFlag(name: string): boolean {
    return this.readUserField(name) === true;
  }

  /**
   * Lector ÚNICO de un campo del objeto user cifrado en sessionStorage (lo
   * comparten `userFlag` y `getCurrentPolipayAccount`). `undefined` si no hay
   * sesión o no se puede leer.
   */
  private readUserField(name: string): unknown {
    const encrypted = this.browserStorage.getSessionStorageItem('user');
    if (!encrypted) return undefined;
    try {
      const decrypted = this.encryption.decrypt(encrypted);
      const user = JSON.parse(decrypted);
      const first = Array.isArray(user) ? user[0] : user;
      return first?.[name];
    } catch {
      return undefined;
    }
  }

  /**
   * Indica si el usuario puede crear transferencias (individual o masiva).
   * Los roles 3, 5 y 6 tienen restringida esta acción.
   */
  canCreateTransfers(): boolean {
    const roleId = this.getRoleId();
    if (roleId === null) return true;
    const restrictedRoles = [3, 5, 6];
    return !restrictedRoles.includes(roleId);
  }
}
